import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, ShoppingBag, Clock, Footprints, Car, Download, FileText } from 'lucide-react';
import { Logo } from '../components/Logo';
import LeadMagnetModal from '../components/LeadMagnetModal';
import Breadcrumbs from '../components/Breadcrumbs';

const META_TITLE = "Flats Near DMart Thergaon Pune | 2 & 3 BHK Within Walking Distance | Nova Homes";
const META_DESCRIPTION = "Looking for a flat near DMart Thergaon? Compare Nova Homes' 2 & 3 BHK projects in Rahatani — just 800m from DMart Thergaon, with full drive-time and walk-time data for daily essentials shoppers.";

const DMartFlatsPage = () => {
  const [leadMagnetOpen, setLeadMagnetOpen] = useState(false);

  useEffect(() => {
    document.title = META_TITLE;
    const desc = document.querySelector('meta[name="description"]');
    const orig = desc?.getAttribute('content');
    if (desc) desc.setAttribute('content', META_DESCRIPTION);

    const schema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "Which Nova Homes project is closest to DMart Thergaon?",
          "acceptedAnswer": { "@type": "Answer", "text": "Silver Woods is the closest at 800 metres — approximately a 10-minute walk or a 2-minute drive from DMart Thergaon." }
        },
        {
          "@type": "Question",
          "name": "Can I walk to DMart from Silver Woods Rahatani?",
          "acceptedAnswer": { "@type": "Answer", "text": "Yes. At 800 metres, DMart Thergaon is comfortably walkable from Silver Woods — most residents complete the walk in 8-10 minutes via the BRTS Road footpath." }
        },
        {
          "@type": "Question",
          "name": "What other daily essentials are near DMart Thergaon?",
          "acceptedAnswer": { "@type": "Answer", "text": "The DMart Thergaon precinct also includes Reliance Smart, Spencer's, Nature's Basket, multiple banks (HDFC, ICICI, SBI), pharmacies, and over 30 restaurants — making it the most complete daily-essentials hub for residents of Rahatani and Kalewadi." }
        }
      ]
    };
    const s = document.createElement('script');
    s.type = 'application/ld+json';
    s.text = JSON.stringify(schema);
    s.setAttribute('data-dmart', 'faq');
    document.head.appendChild(s);
    return () => {
      document.head.removeChild(s);
      if (desc && orig) desc.setAttribute('content', orig);
      document.title = 'Nova Homes Pune | 2 & 3 BHK Flats in Rahatani | Book Site Visit';
    };
  }, []);

  const projects = [
    {
      name: "Silver Woods", slug: "silver-woods",
      type: "2 & 3 BHK Apartments", price: "₹72L – ₹96L",
      walk: "8-10 min", drive: "2 min", distance: "800m",
      image: "/images/silverwoods.webp",
      imageAlt: "Silver Woods — 2 & 3 BHK apartments 800m from DMart Thergaon Pune"
    },
    {
      name: "Aurelia", slug: "aurelia",
      type: "Premium 3 BHK", price: "Starting ₹99.50L",
      walk: "12-15 min", drive: "3 min", distance: "1.1 km",
      image: "/images/aurelia.webp",
      imageAlt: "Aurelia — premium 3 BHK apartments 1.1 km from DMart Thergaon Pune"
    },
    {
      name: "Sneha Avenue", slug: "sneha-avenue",
      type: "2, 3 BHK & Duplex", price: "Starting ₹70L",
      walk: "—", drive: "10 min", distance: "5 km",
      image: "/images/sneha-avenue.webp",
      imageAlt: "Sneha Avenue — 2, 3 BHK and duplex flats 10 min drive from DMart Thergaon Pune"
    }
  ];

  return (
    <div className="min-h-screen bg-[#FAFAF8]" data-testid="dmart-flats-page">
      {/* Header */}
      <header className="bg-[#0a0a0a] border-b border-white/[0.04] sticky top-0 z-40">
        <div className="container-custom h-[72px] flex items-center justify-between">
          <Logo variant="light" />
          <div className="flex items-center gap-4">
            <Link to="/blog" className="hidden md:inline-block text-white/60 hover:text-white text-xs uppercase tracking-[0.2em] font-medium transition-colors">Insights</Link>
            <Link to="/" className="flex items-center gap-1.5 text-white/40 hover:text-white/70 text-sm font-light transition-colors">
              <ArrowLeft size={15} /><span className="hidden sm:inline">Back to Home</span>
            </Link>
          </div>
        </div>
        <div className="container-custom py-4">
          <Breadcrumbs items={[{ label: 'Home', href: '/' }, { label: 'Flats Near DMart Thergaon' }]} />
        </div>
      </header>

      {/* Hero */}
      <section data-testid="dmart-hero" className="relative bg-[#0a0a0a] text-white py-24 md:py-32 overflow-hidden">
        <div className="absolute inset-0 opacity-25">
          <img src="/images/silverwoods.webp" alt="Premium residential building 800m from DMart Thergaon Pune" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-[#0a0a0a]/60 to-[#0a0a0a]/40" />
        </div>
        <div className="container-custom relative max-w-4xl">
          <p className="text-[#C5A46D] text-xs uppercase tracking-[0.3em] font-medium mb-6 flex items-center gap-2">
            <ShoppingBag size={14} /> 800 Metres from your front door
          </p>
          <h1 className="font-heading text-4xl md:text-6xl lg:text-7xl font-semibold leading-[1.05] mb-8">
            2 & 3 BHK Flats Near DMart Thergaon
          </h1>
          <p className="text-white/70 text-lg md:text-xl font-light leading-relaxed mb-8 max-w-3xl">
            Daily essentials shouldn't mean a 30-minute traffic crawl. Three Nova Homes projects in Rahatani and Bhugaon put DMart Thergaon — Pune's busiest neighbourhood retail anchor — well within reach. Compare distances, walk times, and what each address offers.
          </p>
        </div>
      </section>

      {/* Distance comparison */}
      <section data-testid="dmart-distance-comparison" className="py-20 md:py-28 bg-white">
        <div className="container-custom max-w-5xl">
          <p className="text-[#B5A17C] text-xs uppercase tracking-[0.3em] font-medium mb-3">Distance Comparison</p>
          <h2 className="font-heading text-3xl md:text-4xl font-semibold text-[#111] leading-tight mb-10">
            Nova Homes Projects · Distance to DMart Thergaon
          </h2>

          <div className="overflow-x-auto">
            <table className="w-full text-left border border-[#111]/[0.08]">
              <thead className="bg-[#0a0a0a] text-white text-xs uppercase tracking-wider">
                <tr>
                  <th className="py-4 px-5 font-medium">Project</th>
                  <th className="py-4 px-5 font-medium">Configuration</th>
                  <th className="py-4 px-5 font-medium">Distance</th>
                  <th className="py-4 px-5 font-medium hidden md:table-cell">Walk</th>
                  <th className="py-4 px-5 font-medium">Drive</th>
                  <th className="py-4 px-5 font-medium">Price</th>
                </tr>
              </thead>
              <tbody>
                {projects.map((p, i) => (
                  <tr key={p.slug} className={`text-sm ${i % 2 === 0 ? 'bg-white' : 'bg-[#FAFAF8]'} hover:bg-[#FFF8E5] transition-colors`}>
                    <td className="py-4 px-5 font-semibold text-[#111]">
                      <Link to={`/project/${p.slug}`} data-testid={`dmart-row-${p.slug}`} className="hover:text-[#C5A46D] transition-colors">
                        {p.name}
                      </Link>
                    </td>
                    <td className="py-4 px-5 text-[#444] font-light">{p.type}</td>
                    <td className="py-4 px-5 text-[#C5A46D] font-semibold">{p.distance}</td>
                    <td className="py-4 px-5 text-[#444] font-light hidden md:table-cell">{p.walk}</td>
                    <td className="py-4 px-5 text-[#444] font-light">{p.drive}</td>
                    <td className="py-4 px-5 text-[#111] font-medium">{p.price}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="text-[#999] text-xs font-light mt-4 italic">
            Distances are field-measured along walkable / drivable routes. Drive times reflect off-peak conditions.
          </p>
        </div>
      </section>

      {/* Why DMart proximity matters */}
      <section className="py-20 md:py-28 bg-[#FAFAF8]">
        <div className="container-custom max-w-4xl">
          <p className="text-[#B5A17C] text-xs uppercase tracking-[0.3em] font-medium mb-3">Why It Matters</p>
          <h2 className="font-heading text-3xl md:text-4xl font-semibold text-[#111] leading-tight mb-10">
            Why DMart Proximity Quietly Lifts Property Value
          </h2>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              { icon: Footprints, title: "Walkable Daily Essentials", desc: "Groceries, household, festive shopping — all within a 10-minute walk. No car needed for the weekly run." },
              { icon: Clock, title: "Time Compounds", desc: "Saving 30-45 min per shopping trip × 4 trips/month = 24+ hours back per year. Daily-life ROI." },
              { icon: Car, title: "Resale & Rental Premium", desc: "Pune broker data shows 8-12% premium on tenant demand for properties under 1 km from a major DMart or Reliance store." },
            ].map((item, i) => {
              const Icon = item.icon;
              return (
                <div key={i} className="bg-white border border-[#111]/[0.08] p-7">
                  <Icon size={22} className="text-[#C5A46D] mb-4" />
                  <h3 className="font-heading text-base font-semibold text-[#111] mb-2">{item.title}</h3>
                  <p className="text-[#666] text-sm font-light leading-relaxed">{item.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Project cards */}
      <section className="py-20 md:py-28 bg-white">
        <div className="container-custom max-w-5xl">
          <p className="text-[#B5A17C] text-xs uppercase tracking-[0.3em] font-medium mb-3">Available Now</p>
          <h2 className="font-heading text-3xl md:text-4xl font-semibold text-[#111] leading-tight mb-10">
            Choose Your Address
          </h2>

          <div className="grid md:grid-cols-3 gap-6">
            {projects.map((p) => (
              <Link
                key={p.slug}
                to={`/project/${p.slug}`}
                data-testid={`dmart-card-${p.slug}`}
                className="group bg-[#FAFAF8] border border-[#111]/[0.06] hover:border-[#C5A46D]/40 transition-all duration-500 overflow-hidden"
              >
                <div className="aspect-[4/3] overflow-hidden">
                  <img src={p.image} alt={p.imageAlt} loading="lazy" className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-700" />
                </div>
                <div className="p-6">
                  <p className="text-[#C5A46D] text-[10px] uppercase tracking-[0.2em] font-medium mb-2">{p.distance} from DMart</p>
                  <h3 className="font-heading text-xl font-semibold text-[#111] mb-1">{p.name}</h3>
                  <p className="text-[#666] text-sm font-light mb-4">{p.type}</p>
                  <div className="flex items-center justify-between pt-4 border-t border-[#111]/[0.06]">
                    <span className="text-[#111] text-sm font-medium">{p.price}</span>
                    <span className="inline-flex items-center gap-1.5 text-[#C5A46D] text-xs font-medium group-hover:gap-2.5 transition-all">
                      View <ArrowRight size={12} />
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Lead-magnet CTA band */}
      <section className="py-16 md:py-20 bg-[#0a0a0a] text-white">
        <div className="container-custom max-w-4xl">
          <div className="flex flex-col md:flex-row md:items-center gap-6">
            <div className="w-14 h-14 bg-[#C5A46D]/15 text-[#C5A46D] flex items-center justify-center flex-shrink-0 rounded-sm">
              <FileText size={22} />
            </div>
            <div className="flex-1">
              <p className="text-[#C5A46D] text-[10px] uppercase tracking-[0.3em] font-medium mb-2">Free PDF</p>
              <h2 className="font-heading text-2xl md:text-3xl font-semibold mb-2 leading-tight">
                Full Connectivity Map — every landmark, distance & drive-time
              </h2>
            </div>
            <button
              type="button"
              onClick={() => setLeadMagnetOpen(true)}
              data-testid="dmart-download-map-btn"
              className="group inline-flex items-center justify-center gap-2 bg-[#C5A46D] text-[#0a0a0a] py-3.5 px-6 font-medium text-sm tracking-wide hover:bg-[#D4AF37] transition-colors flex-shrink-0"
            >
              <Download size={16} /> Download
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0a0a0a] text-white py-16 border-t border-white/[0.04]">
        <div className="container-custom text-center">
          <Logo variant="light" className="justify-center mb-6" />
          <p className="text-white/40 text-sm font-light mb-2">Nova Homes · Pune, Maharashtra</p>
          <p className="text-white/30 text-xs font-light">© 2026 Nova Homes. All rights reserved.</p>
        </div>
      </footer>

      <LeadMagnetModal open={leadMagnetOpen} onClose={() => setLeadMagnetOpen(false)} source="dmart-flats-page" />
    </div>
  );
};

export default DMartFlatsPage;
